import React from 'react';
import { AlertCircleIcon, ClockIcon, UsersIcon } from 'lucide-react';
import { useCountdown } from '../hooks/useCountdown';
import { offer } from '../data/offer';
interface UrgencyBannerProps {
  /** 'panel' sits inline under a CTA, 'bar' is a full-width sticky strip. */
  variant?: 'panel' | 'bar';
  className?: string;
}
function CountdownClock({
  compact = false
}: {
  compact?: boolean;
}) {
  const {
    hours,
    minutes,
    seconds,
    expired
  } = useCountdown(23.75);
  return <div className={`flex items-center gap-2 rounded-lg border border-white/10 bg-navy-800/80 px-3 py-1.5 tabular-nums ${compact ? '' : 'justify-center'}`} role="timer" aria-live="off" data-id="element-171" data-mp="319jw5nh3vr0n">
      {expired ? <span className="font-display text-sm font-bold text-sun" data-id="element-172" data-mp="3b8sn07gocpis">
          Final hours — price rises today
        </span> : [{
      value: hours,
      unit: 'hr'
    }, {
      value: minutes,
      unit: 'min'
    }, {
      value: seconds,
      unit: 'sec'
    }].map((cell, index) => <React.Fragment key={cell.unit} data-id="element-173" data-mp="27eq15u0qhiov" data-mpi={index}>
            {index > 0 ? <span className="text-white/20" aria-hidden="true" data-id="element-174" data-mp="27gatre3hmrk3">
                :
              </span> : null}
            <span className="flex items-baseline gap-1" data-id="element-175" data-mp="22mot6kalme4n">
              <span className="font-display text-base font-extrabold text-white" data-id="element-176" data-mp="1xo56uis3f0br">
                {cell.value}
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-white/50" data-id="element-177" data-mp="1sremvkd63kl2">
                {cell.unit}
              </span>
            </span>
          </React.Fragment>)}
    </div>;
}
export function UrgencyBanner({
  variant = 'panel',
  className = ''
}: UrgencyBannerProps) {
  if (variant === 'bar') {
    return <div className="sticky top-0 z-50 border-b border-celeste-500/25 bg-navy-900/95 backdrop-blur" data-id="element-178" data-mp="2cdt6zgojfsxw">
        <div className="mx-auto flex max-w-content flex-col items-center gap-x-6 gap-y-2 px-4 py-2.5 text-center sm:px-6 lg:flex-row lg:justify-between lg:text-left" data-id="element-179" data-mp="1509chkpzxki5">
          <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[13px] text-white/80 sm:text-sm" data-id="element-180" data-mp="10xclp6qayi34">
            <span className="flex items-center gap-1.5 font-display text-xs font-extrabold uppercase tracking-[0.16em] text-sun" data-id="element-181" data-mp="12dyvo5li9kp6">
              <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-182" data-mp="si5bq15gioxd" />
              Special offer — limited time only
            </span>
            <span className="hidden text-white/20 lg:inline" aria-hidden="true" data-id="element-183" data-mp="xf5fg5eu3flb">
              |
            </span>
            <span className="flex items-center gap-1.5" data-id="element-184" data-mp="jn8po80n1hs3">
              <AlertCircleIcon className="h-3.5 w-3.5 text-red-400" aria-hidden="true" data-id="element-185" data-mp="oh3ic0y8173b" />
              Only{' '}
              <span className="font-semibold text-white" data-id="element-186" data-mp="9sgqizreqmv7">{offer.spotsRemaining} spots</span>{' '}
              remaining at this price · price increases to{' '}
              <span className="font-semibold text-white" data-id="element-187" data-mp="qpl7w0s4i1v">{offer.priceTomorrow}</span> tomorrow
            </span>
          </p>
          <div className="flex items-center gap-4" data-id="element-188" data-mp="4qzupins5qlh">
            <p className="hidden items-center gap-1.5 text-[13px] text-white/60 xl:flex" data-id="element-189" data-mp="4tvulcj6upck">
              <UsersIcon className="h-3.5 w-3.5 text-celeste-400" aria-hidden="true" data-id="element-190" data-mp="3iazmr1hquwt6" />
              Join {offer.studentCount} students
            </p>
            <CountdownClock compact data-id="element-191" data-mp="3raw40i1t0te7" />
          </div>
        </div>
      </div>;
  }
  return <div className={`rounded-xl border border-celeste-500/30 bg-navy-800/70 p-5 ${className}`} style={{
    paddingTop: "0px",
    paddingBottom: "0px"
  }} data-id="element-192" data-mp="3ma8clv9nyajg">
      <p className="flex items-center gap-2 font-display text-xs font-extrabold uppercase tracking-[0.16em] text-sun" data-id="element-193" data-mp="50ihqnsesqxj">
        <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-194" data-mp="dux0j0c5ejle" />
        Special offer — limited time only
      </p>
      <p className="mt-3 flex items-start gap-2 text-[13px] leading-relaxed text-white/75" data-id="element-195" data-mp="9stg3m7i44nz">
        <AlertCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" aria-hidden="true" data-id="element-196" data-mp="nsuodmq3iedh" />
        <span data-id="element-197" data-mp="iu9m7hra9kme">
          Only <span className="font-semibold text-white" data-id="element-198" data-mp="iv2oa5farly7">{offer.spotsRemaining} spots</span>{' '}
          remaining at this price · price increases to{' '}
          <span className="font-semibold text-white" data-id="element-199" data-mp="12ubaar0fcy8e">{offer.priceTomorrow}</span> tomorrow
        </span>
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-3" data-id="element-200" data-mp="1bog15afk69nx">
        <CountdownClock compact data-id="element-201" data-mp="1bqiw88vkiszi" />
        <p className="flex items-center gap-1.5 text-[13px] text-white/60" data-id="element-202" data-mp="1gn99ypjwhchr">
          <UsersIcon className="h-3.5 w-3.5 text-celeste-400" aria-hidden="true" data-id="element-203" data-mp="1gpddv7hsubi1" />
          Join {offer.studentCount} students
        </p>
      </div>
    </div>;
}